import { useEffect, useRef, useState } from 'react'
import './PatientOverviewQuickNav.css'

const SECTIONS: { id: string; label: string }[] = [
  { id: 'demographics', label: 'Demographics' },
  { id: 'health-identifiers', label: 'Health IDs' },
  { id: 'contact-information', label: 'Contact' },
  { id: 'address', label: 'Address' },
  { id: 'medical-information', label: 'Medical info' },
  { id: 'coverage-schemes', label: 'Coverage & schemes' },
  { id: 'lifestyle-factors', label: 'Lifestyle' },
  { id: 'family-relationships', label: 'Family & relationships' },
  { id: 'notes', label: 'Notes' },
  { id: 'billing', label: 'Billing' },
  { id: 'system-information', label: 'System info' },
]

interface PatientOverviewQuickNavProps {
  availableSections: Record<string, boolean>
}

export function PatientOverviewQuickNav({ availableSections }: PatientOverviewQuickNavProps) {
  const navRef = useRef<HTMLElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const [topPosition, setTopPosition] = useState<number>(280)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const calculatePosition = () => {
      // Find the summary cards container
      const summaryCards = document.querySelector('.patient-summary-cards') as HTMLElement
      if (summaryCards) {
        const cardsRect = summaryCards.getBoundingClientRect()
        const scrollY = window.scrollY || window.pageYOffset
        const calculatedTop = cardsRect.top + scrollY
        
        // Set the top position to align with the top of the cards
        setTopPosition(calculatedTop)
      }
    }

    // Calculate on mount with multiple attempts to ensure DOM is ready
    const attemptCalculate = () => {
      const summaryCards = document.querySelector('.patient-summary-cards')
      if (summaryCards) {
        calculatePosition()
      } else {
        // Retry if cards aren't ready yet
        setTimeout(attemptCalculate, 50)
      }
    }

    attemptCalculate()

    // Recalculate on window resize
    const handleResize = () => {
      setTimeout(calculatePosition, 10)
    }
    window.addEventListener('resize', handleResize)

    // Also recalculate when scrolling (in case header is sticky or layout changes)
    let scrollTimeout: NodeJS.Timeout
    const handleScroll = () => {
      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(calculatePosition, 10)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('scroll', handleScroll)
      clearTimeout(scrollTimeout)
    }
  }, [])

  const handleJump = (id: string) => {
    const el = document.getElementById(id)
    if (el) {
      const headerOffset = 120
      const rect = el.getBoundingClientRect()
      const targetY = rect.top + window.scrollY - headerOffset
      window.scrollTo({ top: targetY, behavior: 'smooth' })
    }
  }

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    const handleClickOutside = (event: MouseEvent) => {
      const navEl = navRef.current
      const toggleEl = toggleRef.current
      if (!navEl || !toggleEl) return
      if (!navEl.contains(event.target as Node) && !toggleEl.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  return (
    <>
      <button
        ref={toggleRef}
        type="button"
        className={`overview-quick-nav-toggle ${isOpen ? 'is-open' : ''}`}
        style={{ top: `${topPosition}px` }}
        aria-expanded={isOpen}
        aria-controls="overview-quick-nav-panel"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        Jump to section
      </button>
      <aside 
        id="overview-quick-nav-panel"
        ref={navRef}
        className={`overview-quick-nav ${isOpen ? 'is-open' : ''}`}
        aria-label="Overview quick navigation"
        style={{ top: `${topPosition}px` }}
      >
        <div className="overview-quick-nav-header">
          <h3 className="overview-quick-nav-title">Jump to section</h3>
          <button
            type="button"
            className="overview-quick-nav-close"
            onClick={() => setIsOpen(false)}
            aria-label="Close jump to section"
          >
            ×
          </button>
        </div>
        <div className="overview-quick-nav-grid">
          {SECTIONS.map((section) => (
            (() => {
              const isEnabled = !!availableSections[section.id]
              return (
                <button
                  key={section.id}
                  type="button"
                  className={`overview-quick-nav-btn ${isEnabled ? '' : 'disabled'}`}
                  onClick={isEnabled ? () => handleJump(section.id) : undefined}
                  disabled={!isEnabled}
                >
                  <span className="nav-icon" aria-hidden="true">
                    <span className="nav-dot" />
                  </span>
                  <span className="nav-label">{section.label}</span>
                </button>
              )
            })()
          ))}
        </div>
      </aside>
    </>
  )
}
